import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { CalendarDaysIcon } from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../services/api'
import { formatDate } from '../health-cycle/cycleStatus'
import { fmt } from '../meal-prep/planFormat'
import PlanMealLogger from './PlanMealLogger'

// Read defensively: this key is shared with the Health Cycle page, and a cache entry
// written by an older tab or an earlier build can still hold the raw axios response.
// Production, 2026-09-17: the Nutrition page died with "(s.data ?? []).find is not a
// function" because one writer cached the response and the other the list.
const asList = (value) => (Array.isArray(value) ? value : Array.isArray(value?.data) ? value.data : [])

const latestFinalized = (cycles) => asList(cycles)
  .filter((c) => c.finalized_at)
  .sort((a, b) => String(b.finalized_at).localeCompare(String(a.finalized_at)))[0] ?? null

// VH-229 refinement: the one weekly plan on the Nutrition page — the week of the selected date from the
// cycle's weeks (same numbers as the cycle report: plan document + current cycle targets), a link to
// the plan and its actions in the cycle, and one-click logging of the plan's meals.
export function WeekPlanCard({ date, onLogged }) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language
  // Same key and cached shape as HealthCyclePanel (the axios response); select the list here.
  // Same key and cached shape as HealthCyclePanel: both cache the unwrapped list.
  const cycles = useQuery({ queryKey: ['healthCycles'], queryFn: () => healthCyclesAPI.list().then((r) => r.data ?? []) })
  const cycle = latestFinalized(cycles.data)
  const weeks = useQuery({
    queryKey: ['healthCycleWeeks', cycle?.id],
    queryFn: () => healthCyclesAPI.listWeeks(cycle.id).then((r) => r.data ?? []),
    enabled: !!cycle,
  })
  if (cycles.isLoading || (cycle && weeks.isLoading)) return null

  const week = asList(weeks.data).find((w) => w.plan_id && w.week_start <= date && date <= w.week_end)
  const link = cycle ? `/health-cycle/cycles/${cycle.id}?tab=nutrition_report` : '/health-cycle'
  const planned = week?.planned ?? {}
  const targets = week?.targets ?? {}

  return (
    <section className="bg-surface-container rounded-lg shadow-md border border-gray-200 p-4 space-y-3" data-testid="week-plan-card">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex items-start gap-2">
          <CalendarDaysIcon className="h-5 w-5 text-purple-700 mt-0.5" aria-hidden="true" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">{t('weekPlanning.card.title')}</h3>
            {week && (
              <p className="text-xs text-gray-600">
                {t('weekPlanning.card.week', { start: formatDate(week.week_start, lang), end: formatDate(week.week_end, lang) })}
              </p>
            )}
          </div>
        </div>
        <Link to={link} className="btn-outline text-sm" data-testid="week-plan-card-open">
          {week?.has_document ? t('weekPlanning.card.open') : t('weekPlanning.card.plan')}
        </Link>
      </div>

      {!cycle && <p className="text-sm text-gray-600">{t('weekPlanning.card.noCycle')}</p>}
      {cycle && !week?.has_document && <p className="text-sm text-gray-600" data-testid="week-plan-card-empty">{t('weekPlanning.card.noPlan')}</p>}
      {week?.has_document && (
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm" data-testid="week-plan-card-totals">
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{t('weekPlanning.card.dayPlan')}</dt>
            <dd className="tabular-nums text-gray-900">{t('weekPlanning.card.kcalProtein', { kcal: fmt(planned.kcal_per_day, lang), protein: fmt(planned.protein_g_per_day, lang) })}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{t('weekPlanning.card.target')}</dt>
            <dd className="tabular-nums text-gray-900">{t('weekPlanning.card.kcalProtein', { kcal: fmt(targets.kcal, lang), protein: fmt(targets.protein_g, lang) })}</dd>
          </div>
          <div>
            <dt className="text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">{t('cycleReports.nutrition.logged')}</dt>
            <dd className="tabular-nums text-gray-900">
              {week.logged?.logged_days > 0
                ? t('cycleReports.nutrition.loggedTotals', { kcal: fmt(week.logged.kcal, lang), protein: fmt(week.logged.protein_g, lang) })
                : '—'}
              <span className="block text-xs text-gray-500">{t('cycleReports.nutrition.loggedDays', { count: week.logged?.logged_days ?? 0 })}</span>
            </dd>
          </div>
        </dl>
      )}
      {week?.has_document && <PlanMealLogger embedded date={date} onLogged={onLogged} />}
    </section>
  )
}

export default WeekPlanCard
